import { selectUserRolePermissions } from '@socialive/common';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_STREAM_DESCRIPTION } from '~/helpers/constants';
import decodeURIComponentSafe from '~/helpers/decodeURIComponentSafe';
import useDisableVideoDownload from '~/helpers/useDisableVideoDownload';
import { heroStreamShape } from '../../helpers/propTypeShapes';
import ShareButton from '../ShareButton';
import BroadcastStreamActionButton from './BroadcastStreamActionButton';
import DeleteRecordingButton from './DeleteRecordingButton';

momentDurationFormatSetup(moment);

const BroadcastStreamListItem = ({
  stream,
  newBroadcastTitle,
  isCurrentUserStreamOwner,
  handleClippingRequest,
  handleDescriptionChange,
  handleEnterKey,
  requestDeleteRecording,
  index,
  broadcastHash,
  broadcastId,
}) => {
  const { canClipVideo, canEditOthersVideos } = useSelector(selectUserRolePermissions);
  const [userRoleType] = useState(isCurrentUserStreamOwner ? '(host)' : '(guest)');
  const [title, setTitle] = useState();

  const originalDescription = useMemo(() => {
    const desc = stream.description ? decodeURIComponentSafe(stream.description) : DEFAULT_STREAM_DESCRIPTION;
    return desc;
  }, [stream.description]);

  const [streamDescription, setStreamDescription] = useState(() => {
    const desc = stream.description ? decodeURIComponentSafe(stream.description) : DEFAULT_STREAM_DESCRIPTION;
    return desc;
  });
  const [displayCheckMark, setDisplayCheckMark] = useState(false);
  const [showDuration, setShowDuration] = useState(true);
  const { disableRightClickFunc } = useDisableVideoDownload();
  const playerRef = useRef();

  const pagePath = `/broadcastdetails/broadcast/${broadcastHash}/${broadcastId}/streams`;
  const thumbnail = stream.thumbnail !== null ? stream.thumbnail : '/FPO_video_thumb.png';

  const canEditContent = canEditOthersVideos;
  const clipTooltipCopy = canClipVideo ? 'Clip' : 'No permission to clip';

  const isVideoFileReady = stream.outfile?.endsWith('mp4') ?? false;
  const isFormatM3u8 = !!stream.m3u8Url;

  const sourceURL = isFormatM3u8 ? stream.m3u8Url : stream.outfile;
  const sourceType = isFormatM3u8 ? 'application/x-mpegURL' : 'video/mp4';

  const handleOnClickToPlay = useCallback((event) => {
    const { videojs } = window;

    if (!playerRef.current) {
      const player = event.target;

      playerRef.current = videojs(player, { controls: true }, () => {
        player.play();
        setShowDuration(false);
      });
    }

    const isVideoPaused = playerRef.current.paused();
    setShowDuration(isVideoPaused);
  }, []);

  const onHandleDescriptionChange = useCallback(
    async (event) => {
      const newDescriptionValue = event.target.innerText;
      const streamId = stream.id;

      if (newDescriptionValue !== streamDescription) {
        const { error } = await handleDescriptionChange(newDescriptionValue, streamId);
        if (error) {
          setStreamDescription(originalDescription);
          return;
        }
        setDisplayCheckMark(true);
      }

      const desc = newDescriptionValue === '' ? DEFAULT_STREAM_DESCRIPTION : newDescriptionValue;
      setStreamDescription(desc);
    },
    [handleDescriptionChange, originalDescription, streamDescription, stream.id]
  );

  const handleOnClick = useCallback((event) => {
    const currentDescription = event.target.innerText;
    if (currentDescription === DEFAULT_STREAM_DESCRIPTION) {
      setStreamDescription('');
    }
  }, []);

  const onDelete = useCallback(() => {
    requestDeleteRecording(stream.id);
  }, [requestDeleteRecording, stream.id]);

  const onClipping = useCallback(() => {
    handleClippingRequest(stream);
  }, [handleClippingRequest, stream]);

  useEffect(
    () => setTitle(stream?.title ? stream.title : `${stream.user.name} ${userRoleType}`),
    [stream, userRoleType]
  );

  useEffect(() => {
    if (!displayCheckMark) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setDisplayCheckMark(false);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [displayCheckMark]);

  useEffect(
    () => () => {
      const player = playerRef.current;
      if (player) {
        player.dispose();
      }
    },
    []
  );

  return (
    <div className="stream">
      <div className="column-spacer">
        <div className="stream-player-container">
          <video
            id={stream.id}
            className="video-js"
            onClick={handleOnClickToPlay}
            preload="none"
            poster={thumbnail}
            onContextMenu={disableRightClickFunc}
          >
            <source src={sourceURL} type={sourceType} />
          </video>
          {showDuration && (
            <div className="stream-duration">{moment.duration(stream.durationSeconds, 'seconds').format()}</div>
          )}
        </div>
      </div>
      <div className="right-column">
        <span className="owner byline">{title}</span>
        <br />
        <span className="started-at byline">
          {`${stream.user.name} ${userRoleType}`} - {moment(stream.startedAt).format('MMM Do YYYY, h:mm a')}
        </span>
        <br />
        &#8203;
        <span
          className="stream-description"
          contentEditable={`${canEditContent}`}
          data-streamid={stream.id}
          data-streamdescription={streamDescription}
          onBlur={onHandleDescriptionChange}
          onKeyDown={handleEnterKey}
          onClick={handleOnClick}
          suppressContentEditableWarning
          role="textbox"
          tabIndex={0}
        >
          {streamDescription}
        </span>
        &#8203;
        {displayCheckMark && <span className="description-success-checkmark active" />}
        <div className="options-menu">
          <ul className="broadcast-options">
            <ShareButton videoId={stream.id} videoType="STREAM" duration={stream.durationSeconds} />
            <BroadcastStreamActionButton
              stream={stream}
              index={index}
              isCurrentUserStreamOwner={isCurrentUserStreamOwner}
              newBroadcastTitle={newBroadcastTitle}
              pagePath={pagePath}
            />

            {isVideoFileReady && (
              <li className="broadcast-option clip" data-for="broadcaststreams-tooltip" data-tip={clipTooltipCopy}>
                <button
                  type="button"
                  disabled={!canClipVideo}
                  onClick={onClipping}
                  title="Clip"
                  aria-label="Clip video"
                />
              </li>
            )}

            <DeleteRecordingButton streamId={stream.id} onDelete={onDelete} />
          </ul>
        </div>
      </div>
    </div>
  );
};

BroadcastStreamListItem.propTypes = {
  handleClippingRequest: PropTypes.func.isRequired,
  handleDescriptionChange: PropTypes.func.isRequired,
  handleEnterKey: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isCurrentUserStreamOwner: PropTypes.bool.isRequired,
  newBroadcastTitle: PropTypes.string.isRequired,
  stream: heroStreamShape.isRequired,
  requestDeleteRecording: PropTypes.func.isRequired,
  broadcastHash: PropTypes.string.isRequired,
  broadcastId: PropTypes.number.isRequired,
};

export default BroadcastStreamListItem;
